import { Alert, Button, Flex, Space, Spin, Typography } from 'antd';
import { photoWizardText } from '@/features/inventorization/photos/photo-wizard-i18n';
import type { CaptureSession } from '@/shared/capture/capture-session';
import { AI_EXTRACTION_JOB_TYPES, AI_EXTRACTION_STATUSES } from '@/shared/ocr/ai-extraction';

export function PhotoWizardExtractionStep({
  error,
  isSubmittingLlm,
  onBack,
  onContinueWithoutLlm,
  onRefresh,
  onSubmitLlm,
  session,
  submittedPhotoIds,
}: {
  error: string | null;
  isSubmittingLlm: boolean;
  onBack: () => void;
  onContinueWithoutLlm: (photoIds: string[]) => Promise<unknown>;
  onRefresh: () => Promise<unknown> | unknown;
  onSubmitLlm: (photoIds: string[]) => Promise<unknown>;
  session: CaptureSession;
  submittedPhotoIds: string[];
}) {
  const latestFailedLlm = getLatestFailedLlmExtraction(session);

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Text type="secondary">{photoWizardText('eyebrow', 'Photos')}</Typography.Text>
        <Typography.Title level={2} style={{ margin: 0 }}>
          {photoWizardText('extraction.title', 'Extracting meter data')}
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          {photoWizardText('extraction.description', 'Photos are resized locally and submitted for LLM extraction.')}
        </Typography.Paragraph>
      </div>
      {error || latestFailedLlm ? (
        <Alert
          closable
          message={error ?? photoWizardText('extraction.failed', 'LLM extraction failed.')}
          showIcon
          type="error"
        />
      ) : (
        <Flex align="center" justify="center" style={{ minHeight: 180 }}>
          <Spin />
        </Flex>
      )}
      <Flex gap="small" justify="space-between" wrap>
        <Button disabled={isSubmittingLlm} onClick={onBack} size="large">
          {photoWizardText('actions.back', 'Back')}
        </Button>
        <Space wrap>
          <Button
            disabled={isSubmittingLlm}
            onClick={() => {
              void onContinueWithoutLlm(submittedPhotoIds).then(() => onRefresh());
            }}
            size="large"
          >
            {photoWizardText('actions.continueWithoutLlm', 'Continue without LLM')}
          </Button>
          <Button
            loading={isSubmittingLlm}
            onClick={() => {
              void onSubmitLlm(submittedPhotoIds).then(result => {
                if (result) {
                  void onRefresh();
                }
              });
            }}
            size="large"
            type="primary"
          >
            {photoWizardText('actions.retryExtraction', 'Retry extraction')}
          </Button>
        </Space>
      </Flex>
    </Space>
  );
}

function getLatestFailedLlmExtraction(session: CaptureSession) {
  return session.aiExtractions
    .filter(
      extraction =>
        extraction.jobType === AI_EXTRACTION_JOB_TYPES.geminiExtraction &&
        extraction.status === AI_EXTRACTION_STATUSES.failed,
    )
    .toSorted((left, right) => right.createdAt.localeCompare(left.createdAt))[0];
}
